import { useEffect, useMemo, useState } from "react";
import { Loader2, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { api, apiErrToStr } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const destinationTypeOptions = [
  { value: "ramal", label: "Ramal" },
  { value: "grupo", label: "Grupo" },
  { value: "fila", label: "Fila" },
  { value: "ivr", label: "URA / IVR" },
  { value: "externo", label: "Externo" },
];

const emptyOption = () => ({
  key: `${Date.now()}-${Math.random()}`,
  digit: "",
  destination_type: "ramal",
  destination_value: "",
});

const defaultForm = (tenantId = "") => ({
  tenant_id: tenantId || "",
  branch_id: "",
  numero: "",
  nome: "",
  audio_path: "",
  mensagem: "",
  timeout_destination_type: "ramal",
  timeout_destino: "",
  invalid_destination_type: "ramal",
  invalid_destino: "",
  tentativas: 3,
  time_condition_id: "",
  time_match_queue: "",
  time_nomatch_queue: "",
  enabled: true,
  options: [emptyOption()],
});

const safeArray = (value) => (Array.isArray(value) ? value : []);

function inferDestinationType(value, maps) {
  const current = String(value || "").trim();
  if (!current) return "ramal";
  if (maps.ramais[current]) return "ramal";
  if (maps.grupos[current]) return "grupo";
  if (maps.filas[current]) return "fila";
  if (maps.ivrs[current]) return "ivr";
  return "externo";
}

function parseOptions(rawOptions, maps) {
  const entries = Object.entries(rawOptions || {});
  if (!entries.length) return [emptyOption()];
  return entries.map(([digit, destination], index) => {
    const value = String(destination || "").trim();
    let type = "externo";
    if (maps.ramais[value]) type = "ramal";
    else if (maps.grupos[value]) type = "grupo";
    else if (maps.filas[value]) type = "fila";
    else if (maps.ivrs[value]) type = "ivr";
    return {
      key: `${digit}-${index}`,
      digit,
      destination_type: type,
      destination_value: value,
    };
  });
}

function normalizeOptions(options) {
  return safeArray(options)
    .map((item) => ({
      digit: String(item.digit || "").trim(),
      destination_value: String(item.destination_value || "").trim(),
    }))
    .filter((item) => item.digit && item.destination_value);
}

export default function IVRs() {
  const { user, activeTenantId } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const requiresTenantSelection = isSuperAdmin && !activeTenantId;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState(defaultForm(activeTenantId || user?.tenant_id));
  const [tenants, setTenants] = useState([]);
  const [ramais, setRamais] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [filas, setFilas] = useState([]);
  const [ivrs, setIvrs] = useState([]);
  const [timeConditions, setTimeConditions] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const unscoped = isSuperAdmin ? { skipTenantScope: true } : undefined;
      const requests = [
        api.get("/ivrs", unscoped),
        api.get("/ramais", unscoped),
        api.get("/grupos", unscoped),
        api.get("/filas", unscoped),
        api.get("/time-conditions", unscoped),
        isSuperAdmin
          ? api.get("/tenants", { skipTenantScope: true })
          : api.get("/tenants/current").then(({ data }) => ({ data: data ? [data] : [] })),
      ];
      const [ivrRes, ramalRes, grupoRes, filaRes, conditionRes, tenantRes] = await Promise.all(requests);
      const ivrRows = ivrRes.data || [];
      const tenantScopedRows = requiresTenantSelection
        ? []
        : isSuperAdmin && activeTenantId
        ? ivrRows.filter((row) => row?.tenant_id === activeTenantId)
        : ivrRows;
      setRows(tenantScopedRows);
      setRamais(ramalRes.data || []);
      setGrupos(grupoRes.data || []);
      setFilas(filaRes.data || []);
      setIvrs(ivrRows);
      setTimeConditions(conditionRes.data || []);
      setTenants(tenantRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [isSuperAdmin, activeTenantId, requiresTenantSelection]);

  const tenantMap = useMemo(
    () => Object.fromEntries((tenants || []).map((row) => [row.id, row.nome])),
    [tenants],
  );
  const tenantById = useMemo(
    () => Object.fromEntries((tenants || []).map((row) => [row.id, row])),
    [tenants],
  );
  const branchesForTenant = (tenantId) => tenantById[tenantId || user?.tenant_id]?.branches || [];

  const maps = useMemo(
    () => ({
      ramais: Object.fromEntries((ramais || []).map((row) => [String(row.numero), row.nome])),
      grupos: Object.fromEntries((grupos || []).map((row) => [String(row.numero), row.nome])),
      filas: Object.fromEntries((filas || []).map((row) => [String(row.numero), row.nome])),
      ivrs: Object.fromEntries((ivrs || []).map((row) => [String(row.numero), row.nome])),
    }),
    [ramais, grupos, filas, ivrs],
  );

  const listForType = (type, tenantId, branchId, currentNumber) => {
    const filterTenant = (row) =>
      row.enabled !== false &&
      (!tenantId || row.tenant_id === tenantId) &&
      (!branchId || String(row.branch_id || "") === String(branchId)) &&
      String(row.numero) !== String(currentNumber || "");
    if (type === "ramal") return ramais.filter(filterTenant).map((row) => ({ value: String(row.numero), label: `${row.numero} - ${row.nome}` }));
    if (type === "grupo") return grupos.filter(filterTenant).map((row) => ({ value: String(row.numero), label: `${row.numero} - ${row.nome}` }));
    if (type === "fila") return filas.filter(filterTenant).map((row) => ({ value: String(row.numero), label: `${row.numero} - ${row.nome}` }));
    if (type === "ivr") return ivrs.filter(filterTenant).map((row) => ({ value: String(row.numero), label: `${row.numero} - ${row.nome}` }));
    return [];
  };

  const openForm = (row = null) => {
    setError("");
    setEditing(row);
    if (!row) {
      setForm(defaultForm(activeTenantId || user?.tenant_id));
      setShowForm(true);
      return;
    }
    setForm({
      tenant_id: row.tenant_id || user?.tenant_id || "",
      branch_id: row.branch_id || "",
      numero: row.numero || "",
      nome: row.nome || "",
      audio_path: row.audio_path || "",
      mensagem: row.mensagem || "",
      timeout_destination_type: inferDestinationType(row.timeout_destino, maps),
      timeout_destino: row.timeout_destino || "",
      invalid_destination_type: inferDestinationType(row.invalid_destino, maps),
      invalid_destino: row.invalid_destino || "",
      tentativas: row.tentativas || 3,
      time_condition_id: row.time_condition_id || "",
      time_match_queue: row.time_match_queue || "",
      time_nomatch_queue: row.time_nomatch_queue || "",
      enabled: row.enabled !== false,
      options: parseOptions(row.opcoes, maps),
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setError("");
  };

  const updateOption = (key, patch) => {
    setForm((current) => ({
      ...current,
      options: current.options.map((item) =>
        item.key === key
          ? {
              ...item,
              ...patch,
            }
          : item,
      ),
    }));
  };

  const addOption = () => {
    setForm((current) => ({ ...current, options: [...safeArray(current.options), emptyOption()] }));
  };

  const removeOption = (key) => {
    setForm((current) => {
      const next = current.options.filter((item) => item.key !== key);
      return { ...current, options: next.length ? next : [emptyOption()] };
    });
  };

  const uploadAudio = async (file, target = "audio_path") => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const data = new FormData();
      data.append("file", file);
      const response = await api.post("/anuncios/upload-audio", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((current) => ({ ...current, [target]: response.data.path || "" }));
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const normalized = normalizeOptions(form.options);
      const payload = {
        tenant_id: form.tenant_id || user?.tenant_id || "",
        branch_id: String(form.branch_id || "").trim() || null,
        numero: String(form.numero || "").trim(),
        nome: String(form.nome || "").trim(),
        audio_path: form.audio_path || "",
        mensagem: form.mensagem || "",
        timeout_destino: String(form.timeout_destino || "").trim() || null,
        invalid_destino: String(form.invalid_destino || "").trim() || null,
        tentativas: Number(form.tentativas || 3),
        time_condition_id: String(form.time_condition_id || "").trim() || null,
        time_match_queue: String(form.time_match_queue || "").trim() || null,
        time_nomatch_queue: String(form.time_nomatch_queue || "").trim() || null,
        enabled: form.enabled !== false,
        opcoes: Object.fromEntries(normalized.map((item) => [item.digit, item.destination_value])),
      };
      if (editing) await api.put(`/ivrs/${editing.id}`, payload);
      else await api.post("/ivrs", payload);
      closeForm();
      await load();
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Confirma exclusao da URA ${row.numero}?`)) return;
    await api.delete(`/ivrs/${row.id}`);
    await load();
  };

  return (
    <div className="space-y-5" data-testid="ivrs-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Menu de atendimento por teclas</div>
          <h1 className="text-2xl font-semibold tracking-tight">URA / IVR</h1>
          {requiresTenantSelection && (
            <div className="mt-2 text-xs text-[color:var(--accent-amber)] font-mono">
              Selecione um cliente no topo para administrar as URAs dele.
            </div>
          )}
        </div>
        <button className="sbc-btn sbc-btn-primary" onClick={() => openForm()} disabled={requiresTenantSelection}>
          <Plus size={14} /> Adicionar
        </button>
      </div>

      <div className="sbc-card overflow-hidden">
        <div className="overflow-auto max-h-[70vh]">
          <table className="sbc-table">
            <thead>
              <tr>
                {isSuperAdmin && <th>Cliente</th>}
                <th>Filial</th>
                <th>Numero</th>
                <th>Nome</th>
                <th>Opcoes</th>
                <th>Sem resposta</th>
                <th>Invalido</th>
                <th>Status</th>
                <th className="w-24 text-right">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={isSuperAdmin ? 9 : 8} className="py-8 text-center text-[color:var(--text-muted)]">Carregando...</td>
                </tr>
              ) : !rows.length ? (
                <tr>
                  <td colSpan={isSuperAdmin ? 9 : 8} className="py-8 text-center text-[color:var(--text-muted)]">Nenhuma URA cadastrada.</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    {isSuperAdmin && <td className="font-medium">{tenantMap[row.tenant_id] || row.tenant_id || "-"}</td>}
                    <td>{row.branch_name || "Escopo geral"}</td>
                    <td className="font-mono">{row.numero}</td>
                    <td>{row.nome}</td>
                    <td className="font-mono text-xs">
                      {Object.entries(row.opcoes || {}).length
                        ? Object.entries(row.opcoes || {})
                            .map(([digit, destination]) => `${digit}:${destination}`)
                            .join(", ")
                        : "-"}
                    </td>
                    <td className="text-xs">{row.timeout_destino || "Finalizar"}</td>
                    <td className="text-xs">{row.invalid_destino || "Finalizar"}</td>
                    <td>
                      {row.enabled ? (
                        <span className="sbc-tag tag-online">ativo</span>
                      ) : (
                        <span className="sbc-tag tag-offline">inativo</span>
                      )}
                    </td>
                    <td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <button className="sbc-btn" onClick={() => openForm(row)} title="Editar">
                          <Pencil size={12} />
                        </button>
                        <button className="sbc-btn sbc-btn-danger" onClick={() => remove(row)} title="Excluir">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4" onClick={closeForm}>
          <div className="sbc-card fade-in w-full max-w-5xl p-6" onClick={(event) => event.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{editing ? "Editar" : "Nova"}</div>
                <h2 className="text-lg font-semibold">URA / IVR</h2>
              </div>
              <button className="sbc-btn" onClick={closeForm}>
                <X size={14} />
              </button>
            </div>

            <form onSubmit={save} className="space-y-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {isSuperAdmin && (
                  <div>
                    <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">Cliente</label>
                    <select
                      className="sbc-input"
                      value={form.tenant_id || ""}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          tenant_id: event.target.value,
                          branch_id: "",
                          timeout_destination_type: "ramal",
                          timeout_destino: "",
                          invalid_destination_type: "ramal",
                          invalid_destino: "",
                          time_condition_id: "",
                          options: current.options.map((item) => ({ ...item, destination_value: "" })),
                        }))
                      }
                    >
                      <option value="">-- selecione o cliente --</option>
                      {tenants.map((tenant) => (
                        <option key={tenant.id} value={tenant.id}>
                          {tenant.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">Unidade / filial</label>
                  <select
                    className="sbc-input"
                    value={form.branch_id || ""}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        branch_id: event.target.value,
                        timeout_destino: "",
                        invalid_destino: "",
                        time_match_queue: "",
                        time_nomatch_queue: "",
                        options: current.options.map((item) => ({ ...item, destination_value: "" })),
                      }))
                    }
                  >
                    <option value="">Escopo geral do cliente</option>
                    {branchesForTenant(form.tenant_id)
                      .filter((branch) => branch.enabled !== false)
                      .map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.nome}
                        </option>
                      ))}
                  </select>
                  <div className="mt-1 text-[10px] text-[color:var(--text-muted)]">Destinos desta URA ficam limitados a esta filial.</div>
                </div>

                <div>
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">Numero da URA</label>
                  <input className="sbc-input font-mono" value={form.numero} onChange={(event) => setForm({ ...form, numero: event.target.value })} placeholder="800" />
                </div>

                <div>
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">Nome</label>
                  <input className="sbc-input" value={form.nome} onChange={(event) => setForm({ ...form, nome: event.target.value })} placeholder="Atendimento principal" />
                </div>

                <div>
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">Tentativas</label>
                  <input className="sbc-input font-mono" type="number" value={form.tentativas} onChange={(event) => setForm({ ...form, tentativas: Number(event.target.value || 3) })} />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">Audio WAV/MP3</label>
                  <input className="sbc-input" type="file" accept=".wav,.wave,.mp3,audio/wav,audio/mpeg" disabled={uploading} onChange={(event) => uploadAudio(event.target.files?.[0])} />
                  {uploading && <div className="mt-1 text-[10px] font-mono text-[color:var(--text-muted)]">enviando arquivo...</div>}
                  {form.audio_path && <div className="mt-1 break-all font-mono text-[10px] text-[color:var(--accent-green)]">{form.audio_path}</div>}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">Mensagem auxiliar</label>
                  <textarea className="sbc-input min-h-[96px]" value={form.mensagem || ""} onChange={(event) => setForm({ ...form, mensagem: event.target.value })} placeholder="Mensagem descritiva para essa URA." />
                </div>
              </div>

              <div className="rounded border border-[color:var(--border-default)] p-4 space-y-3">
                <div><h3 className="text-sm font-semibold">Condicao de tempo</h3><p className="text-[10px] text-[color:var(--text-muted)]">Dentro do horario a URA atende normalmente. Fora do horario usa o destino Fora da condicao selecionada.</p></div>
                <select className="sbc-input" value={form.time_condition_id || ""} onChange={(event) => setForm({ ...form, time_condition_id: event.target.value })}>
                  <option value="">Sem condicao - funcionamento 24 horas</option>
                  {timeConditions.filter((row) => row.enabled !== false && (!form.tenant_id || row.tenant_id === form.tenant_id)).map((row) => <option key={row.id} value={row.id}>{row.nome}</option>)}
                </select>
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">Fila dentro do horario</label>
                    <select
                      className="sbc-input"
                      value={form.time_match_queue || ""}
                      onChange={(event) => setForm({ ...form, time_match_queue: event.target.value })}
                    >
                      <option value="">Usar destino da condicao</option>
                      {filas
                        .filter((row) => row.enabled !== false && (!form.tenant_id || row.tenant_id === form.tenant_id) && (!form.branch_id || String(row.branch_id || "") === String(form.branch_id)))
                        .map((row) => (
                          <option key={`tmq-${row.id || row.numero}`} value={String(row.numero)}>
                            {row.numero} - {row.nome}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">Fila fora do horario</label>
                    <select
                      className="sbc-input"
                      value={form.time_nomatch_queue || ""}
                      onChange={(event) => setForm({ ...form, time_nomatch_queue: event.target.value })}
                    >
                      <option value="">Usar destino fora da condicao</option>
                      {filas
                        .filter((row) => row.enabled !== false && (!form.tenant_id || row.tenant_id === form.tenant_id) && (!form.branch_id || String(row.branch_id || "") === String(form.branch_id)))
                        .map((row) => (
                          <option key={`tnq-${row.id || row.numero}`} value={String(row.numero)}>
                            {row.numero} - {row.nome}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="rounded border border-[color:var(--border-default)]">
                <div className="flex items-center justify-between border-b border-[color:var(--border-default)] px-4 py-3">
                  <div>
                    <h3 className="text-sm font-semibold">Inserir nova opcao</h3>
                    <p className="text-[10px] text-[color:var(--text-muted)]">Defina a tecla, escolha se o destino sera URA, grupo, fila, ramal ou externo e depois selecione o cadastro correspondente.</p>
                  </div>
                  <button type="button" className="sbc-btn" onClick={addOption}>
                    <Plus size={13} /> Inserir nova opcao
                  </button>
                </div>

                <div className="space-y-3 p-4">
                  {safeArray(form.options).map((option, index) => {
                    const destinationOptions = listForType(option.destination_type, form.tenant_id || user?.tenant_id, form.branch_id, form.numero);
                    return (
                      <div key={option.key} className="grid grid-cols-1 gap-3 rounded border border-[color:var(--border-default)] p-3 md:grid-cols-12">
                        <div className="md:col-span-2">
                          <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">Tecla</label>
                          <input
                            className="sbc-input font-mono"
                            value={option.digit}
                            onChange={(event) => updateOption(option.key, { digit: event.target.value })}
                            placeholder={String(index + 1)}
                          />
                        </div>

                        <div className="md:col-span-3">
                          <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">Tipo de destino</label>
                          <select
                            className="sbc-input"
                            value={option.destination_type}
                            onChange={(event) =>
                              updateOption(option.key, {
                                destination_type: event.target.value,
                                destination_value: "",
                              })
                            }
                          >
                            {destinationTypeOptions.map((item) => (
                              <option key={item.value} value={item.value}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="md:col-span-6">
                          <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">Cadastro de destino</label>
                          {option.destination_type === "externo" ? (
                            <input
                              className="sbc-input font-mono"
                              value={option.destination_value}
                              onChange={(event) => updateOption(option.key, { destination_value: event.target.value })}
                              placeholder="Numero externo ou destino manual"
                            />
                          ) : (
                            <select
                              className="sbc-input"
                              value={option.destination_value}
                              onChange={(event) => updateOption(option.key, { destination_value: event.target.value })}
                            >
                              <option value="">-- selecione --</option>
                              {destinationOptions.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>

                        <div className="flex items-end md:col-span-1">
                          <button type="button" className="sbc-btn sbc-btn-danger w-full" onClick={() => removeOption(option.key)} title="Excluir opcao">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">Destino sem resposta</label>
                  <div className="space-y-2">
                    <select
                      className="sbc-input"
                      value={form.timeout_destination_type || "ramal"}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          timeout_destination_type: event.target.value,
                          timeout_destino: "",
                        })
                      }
                    >
                      {destinationTypeOptions.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                    {form.timeout_destination_type === "externo" ? (
                      <input
                        className="sbc-input font-mono"
                        value={form.timeout_destino || ""}
                        onChange={(event) => setForm({ ...form, timeout_destino: event.target.value })}
                        placeholder="Numero externo ou destino manual"
                      />
                    ) : (
                      <select
                        className="sbc-input"
                        value={form.timeout_destino || ""}
                        onChange={(event) => setForm({ ...form, timeout_destino: event.target.value })}
                      >
                        <option value="">-- selecione --</option>
                        {listForType(form.timeout_destination_type, form.tenant_id || user?.tenant_id, form.branch_id, form.numero).map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--text-muted)]">Destino para tecla invalida</label>
                  <div className="space-y-2">
                    <select
                      className="sbc-input"
                      value={form.invalid_destination_type || "ramal"}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          invalid_destination_type: event.target.value,
                          invalid_destino: "",
                        })
                      }
                    >
                      {destinationTypeOptions.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                    {form.invalid_destination_type === "externo" ? (
                      <input
                        className="sbc-input font-mono"
                        value={form.invalid_destino || ""}
                        onChange={(event) => setForm({ ...form, invalid_destino: event.target.value })}
                        placeholder="Numero externo ou destino manual"
                      />
                    ) : (
                      <select
                        className="sbc-input"
                        value={form.invalid_destino || ""}
                        onChange={(event) => setForm({ ...form, invalid_destino: event.target.value })}
                      >
                        <option value="">-- selecione --</option>
                        {listForType(form.invalid_destination_type, form.tenant_id || user?.tenant_id, form.branch_id, form.numero).map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-2 pt-1.5">
                <input type="checkbox" checked={!!form.enabled} onChange={(event) => setForm({ ...form, enabled: event.target.checked })} className="h-4 w-4" />
                <span className="text-sm text-[color:var(--text-secondary)]">{form.enabled ? "Ativado" : "Desativado"}</span>
              </label>

              {error && (
                <div className="rounded-sm border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 font-mono text-xs text-[color:var(--accent-red)]">
                  ! {error}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button type="button" className="sbc-btn" onClick={closeForm}>
                  Cancelar
                </button>
                <button type="submit" className="sbc-btn sbc-btn-primary" disabled={saving} onClick={save}>
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {saving ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
